import _ from 'lodash';
import { DateTime as LuxonDateTime, FixedOffsetZone, Settings } from 'luxon';
import dayjs from './dayjs/index.js';
import DateTime from './DateTime.js';

// Le premier jour de la semaine est dimanche et non lundi.
// Impact sur les fonctions weekday, weekNumber, startOf et endOf.

const units = ['day', 'week', 'month', 'quarter', 'year'];
let todayMock;

export default class UTCDate {
  static set defaultLocale(value) {
    dayjs.locale(value);
  }

  static set timezone(value) {
    Settings.defaultZoneName = value;
  }

  static get timezone() {
    return Settings.defaultZoneName;
  }

  static set fixedToday(value) {
    todayMock = value;
  }

  static get today() {
    if (todayMock) {
      return todayMock;
    }
    const { year, month, day } = LuxonDateTime.local();
    const dt = LuxonDateTime.utc(year, month, day);
    return new UTCDate(dt.ts);
  }

  static toDateString(date) {
    const fullYear = date.getFullYear(),
      month = ('0' + (date.getMonth() + 1)).slice(-2),
      dayOfTheMonth = ('0' + date.getDate()).slice(-2);

    return [fullYear, month, dayOfTheMonth].join('-');
  }

  static fromISO(iso) {
    const pos = iso.indexOf('T');
    const dateStr = pos === -1 ? iso : iso.substring(0, pos);
    const dt = LuxonDateTime.fromISO(dateStr, { zone: FixedOffsetZone.utcInstance });
    return new UTCDate(dt.ts);
  }

  constructor(date) {
    if (_.isNumber(date)) {
      this._d = new LuxonDateTime({ ts: date, zone: FixedOffsetZone.utcInstance });
    } else if (date === undefined) {
      this._d = UTCDate.today._d;
    } else if (_.isString(date)) {
      this._d = UTCDate.fromISO(date)._d;
    } else if (date instanceof Date) {
      this._d = UTCDate.fromISO(UTCDate.toDateString(date))._d;
    } else {
      throw new Error('Invalid date value ' + date);
    }
  }

  get ts() {
    return this._d.ts;
  }

  get year() {
    return this._d.year;
  }

  get month() {
    return this._d.month;
  }

  get day() {
    return this._d.day;
  }

  get quarter() {
    return this._d.quarter;
  }

  get weekday() {
    return this.add(1, 'day')._d.weekday;
  }

  get weekNumber() {
    return this.add(1, 'day')._d.weekNumber;
  }

  toDateTime() {
    const dt = new DateTime(this._d.ts);
    return dt.subtract(dt._dt.offset, 'minute');
  }

  toDate() {
    const { year, month, day } = this._d;
    return new Date(year, month - 1, day);
  }

  toISO() {
    return this._d.toISO();
  }

  toJSON() {
    return this._d.toJSON();
  }

  toString() {
    return this._d.toString();
  }

  valueOf() {
    return this._d.valueOf();
  }

  format(formatStr, locale) {
    const value = dayjs.utc(this._d.ts);

    return (locale ? value.locale(locale) : value).format(formatStr);
  }

  fromNow(withoutSuffix) {
    return dayjs(this._d.ts).fromNow(withoutSuffix);
  }

  from(compared, withoutSuffix) {
    return dayjs(this._d.ts).from(compared, withoutSuffix);
  }

  isEqual(date) {
    return this._d.hasSame(date._d, 'day');
  }

  add(number, unit) {
    if (!units.includes(unit)) {
      throw new Error('Invalid unit ' + unit);
    }
    return new UTCDate(this._d.plus({ [unit + 's']: number }).ts);
  }

  subtract(number, unit) {
    if (!units.includes(unit)) {
      throw new Error('Invalid unit ' + unit);
    }
    return new UTCDate(this._d.minus({ [unit + 's']: number }).ts);
  }

  startOf(unit) {
    if (!units.includes(unit)) {
      throw new Error('Invalid unit ' + unit);
    }
    return unit === 'week'
      ? new UTCDate(this.add(1, 'day')._d.startOf(unit).ts).subtract(1, 'day')
      : new UTCDate(this._d.startOf(unit).ts);
  }

  endOf(unit) {
    if (!units.includes(unit)) {
      throw new Error('Invalid unit ' + unit);
    }
    return unit === 'week'
      ? new UTCDate(this.add(1, 'day')._d.endOf(unit).ts).subtract(1, 'day')
      : new UTCDate(this._d.endOf(unit).ts);
  }

  diff(date, unit, floating = false) {
    if (!units.includes(unit)) {
      throw new Error('Invalid unit ' + unit);
    }
    const other = date instanceof UTCDate ? date : new UTCDate(date);
    const duration = this._d.diff(other._d, unit + 's');

    if (floating) {
      return _.round(duration.values[unit + 's'], 1);
    } else {
      return _.floor(duration.values[unit + 's']);
    }
  }

  isBetween(date1, date2, inclusive = false) {
    const d1 = date1 instanceof UTCDate ? date1 : new UTCDate(date1);
    const d2 = date2 instanceof UTCDate ? date2 : new UTCDate(date2);
    return inclusive ? this >= d1 && this <= d2 : this > d1 && this < d2;
  }
}
